import {
  TimelineStart,
  StepIndicator,
  TimelineIndicator,
  TimelineEnd,
} from "~/molecules/WorkflowTimelineIndicators";
import { useTranslation } from "react-i18next";
import {
  WorkflowStepStatuses,
  type DocumentDetailsVersion,
  type WorkflowDetails,
} from "~/api/types";
import { Link } from "react-router";
import { Button } from "~/atoms/button";
import { Archive, BookLock } from "lucide-react";
import { useState, useEffect } from "react";
import { formatDate } from "~/helpers/date";
import { SetAdoptionDateModal } from "~/molecules/SetAdoptionDateModal";
import { PublishModal } from "~/molecules/PublishModal";
import { WorkflowOverviewStepControlContainer } from "~/molecules/WorkflowOverviewStepControlContainer";
import { WorkflowOverviewStep } from "~/molecules/WorkflowOverviewStep";
import WorkflowDescription from "~/molecules/WorkflowDescription";
import {
  canEditWorkflow,
  canManageVersion,
  canPublishVersion,
  canSetWorkflowAdoptionDate,
  getWorkflowStepControlPermissions,
  isDraftVersion,
} from "~/helpers/documentPermissions";
import NoResult from "~/molecules/NoResult";
import WorkflowDates from "~/molecules/WorkflowDates";

interface Props {
  workflow?: WorkflowDetails;
  selectedVersion: DocumentDetailsVersion;
  onPublish: (versionId: number) => void;
  onChange: () => void;
}

export const WorkflowOverview = ({
  workflow,
  selectedVersion,
  onPublish,
  onChange,
}: Props) => {
  const { t } = useTranslation();

  const isDraft = isDraftVersion(selectedVersion);

  const [isWorkflowExpanded, setIsWorkflowExpanded] = useState(isDraft);

  useEffect(() => {
    setIsWorkflowExpanded(isDraft);
  }, [isDraft]);

  const canEditCurrentWorkflow = canEditWorkflow(selectedVersion);
  const canManage = canManageVersion(selectedVersion);

  if (workflow === undefined) {
    return <NoResult />;
  }

  const lastStep = workflow.steps[workflow.steps.length - 1];
  const lastStepIndex = workflow.steps.length - 1;
  const isLastStepActive = lastStep?.status === WorkflowStepStatuses.active;

  const showSetAdoptionDate = canSetWorkflowAdoptionDate(
    selectedVersion,
    isLastStepActive,
  );

  const { showPublishButton } = getWorkflowStepControlPermissions({
    canManage,
    canPublish: canPublishVersion(selectedVersion),
    isWorkflowStarted: workflow.isOngoing,
    isActiveStep: isLastStepActive,
    isFirstStep: lastStepIndex === 0,
    isLastStep: true,
    versionStatus: selectedVersion.status,
  });

  return (
    <div className="flex flex-col gap-4">
      {canEditCurrentWorkflow && (
        <Button variant="outline" asChild>
          <Link to={`/workflows/${workflow.id}/edit`}>
            {t(($) => $.commons.workflow.editWorkflow)}
          </Link>
        </Button>
      )}
      <WorkflowDates workflow={workflow} />
      <WorkflowDescription description={workflow.description} />
      <div className="relative">
        <div className="bg-primary/40 absolute top-0 bottom-0 left-3.5 z-1 w-0.5"></div>
        <div className="space-y-4">
          <TimelineStart startedAt={workflow.stepInstances[0]?.createdAt} />
          {isWorkflowExpanded ? (
            workflow.steps.map((step, index) => (
              <WorkflowOverviewStepControlContainer
                key={step.id}
                step={step}
                stepIndex={index}
                totalSteps={workflow.steps.length}
                workflowId={workflow.id}
                selectedVersion={selectedVersion}
                isWorkflowStarted={workflow.isOngoing}
                onChange={onChange}
              >
                <div className="flex items-start gap-2 py-2">
                  <StepIndicator status={step.status} />
                  <div className="min-w-0 grow">
                    <WorkflowOverviewStep
                      step={step}
                      stepInstances={workflow.stepInstances}
                      selectedVersionStatus={selectedVersion.status}
                      onUserResponse={onChange}
                    />
                  </div>
                </div>
              </WorkflowOverviewStepControlContainer>
            ))
          ) : (
            <div className="flex items-center gap-5 py-2">
              <StepIndicator
                value="..."
                status={WorkflowStepStatuses.upcoming}
              />
              <div className="flex grow items-center text-sm italic">
                {workflow.steps.length}{" "}
                {t(($) => $.commons.workflow.completedSteps)}
              </div>
            </div>
          )}

          {!isDraft && (
            <div className="ml-10 flex justify-center">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsWorkflowExpanded(!isWorkflowExpanded)}
              >
                {isWorkflowExpanded
                  ? t(($) => $.commons.workflow.hideSteps)
                  : t(($) => $.commons.workflow.showSteps)}
              </Button>
            </div>
          )}

          {showSetAdoptionDate && (
            <TimelineIndicator
              icon={BookLock}
              text={`${t(($) => $.commons.document.adopted)} ${formatDate(selectedVersion.adoptedAt, "date")}`}
              variant={selectedVersion.adoptedAt ? "primary" : "secondary"}
            />
          )}
          {showSetAdoptionDate && (
            <div className="ml-10 flex justify-center">
              <SetAdoptionDateModal
                adoptedAt={selectedVersion.adoptedAt}
                documentVersionId={selectedVersion.id}
                onChange={onChange}
              />
            </div>
          )}

          {showPublishButton && (
            <div className="ml-10 flex justify-center">
              <PublishModal
                onConfirm={onPublish}
                canManage={canManage}
                workflow={workflow}
                selectedVersion={selectedVersion}
                scheduleReviewPeriod={workflow.document.scheduleReviewPeriod}
                lastStep={lastStep}
              />
            </div>
          )}

          <TimelineEnd
            reviewTo={workflow.documentVersion.reviewTo}
            publishedAt={workflow.documentVersion.publishedAt}
          />

          {!isDraft && (
            <TimelineIndicator
              icon={Archive}
              text={`${t(($) => $.commons.documentStatus.archived)} ${formatDate(selectedVersion.archivedAt, "date")}`}
              variant={selectedVersion.archivedAt ? "primary" : "secondary"}
            />
          )}
        </div>
      </div>
    </div>
  );
};
